"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  resolveChartPronounsForDropdown,
  PRONOUN_DROPDOWN_OPTIONS,
} from "../../utils/inferPronouns";

// Improved response extractor that handles multiple API shapes
function extractSoapNoteFromMagicEditorResponse(response) {
  // Try common response structures
  const soapNote =
    response?.data?.body?.data?.soapNote ||
    response?.data?.data?.soapNote ||
    response?.body?.data?.soapNote ||
    response?.data?.soapNote ||
    response?.soapNote ||
    null;
  const ok = !!(soapNote || response?.status === 200);
  const message =
    response?.data?.body?.message ||
    response?.data?.message ||
    response?.message ||
    "";
  return { ok, soapNote, message };
}

export default function SoapNotePronounBar({
  soapNote,
  patientContext,
  onStartProcessing,
  onEndProcessing,
  onUpdateSoapNote,
  aiService,
  onCancel,
}) {
  const chartPronouns = useMemo(
    () => resolveChartPronounsForDropdown(soapNote, patientContext),
    [soapNote, patientContext],
  );

  // chartPronounId can be null (no pronoun saved yet) or "he/him", "she/her", "they/them"
  const chartPronounId = chartPronouns.pronouns;
  const [selectedPronouns, setSelectedPronouns] = useState(chartPronounId);
  const [isProcessing, setIsProcessing] = useState(false);

  // When the external pronoun changes (e.g., after successful update), sync the selection
  useEffect(() => {
    setSelectedPronouns(chartPronounId);
  }, [chartPronounId]);

  const handleApply = async () => {
    if (!soapNote?.soapNoteId || isProcessing) return;

    // If no pronoun selected, do nothing (button should be disabled, but guard)
    if (!selectedPronouns) {
      toast.error("Please select a pronoun set");
      return;
    }

    // If already the same, close card and notify
    if (selectedPronouns === chartPronounId) {
      toast.info("This chart already uses that pronoun set.");
      onCancel?.();
      return;
    }

    setIsProcessing(true);
    onStartProcessing?.();

    try {
      const response = await aiService.correctSoapNotePronouns(
        soapNote.soapNoteId,
        selectedPronouns,
      );

      const { ok, soapNote: updatedNote, message } =
        extractSoapNoteFromMagicEditorResponse(response);

      if (!ok) {
        throw new Error(message || "Failed to update pronouns");
      }

      if (updatedNote && onUpdateSoapNote) {
        // Ensure metadata exists and store the applied pronoun
        if (!updatedNote.metadata) updatedNote.metadata = {};
        updatedNote.metadata.pronouns = selectedPronouns;
        // Update the parent's SOAP note state (triggers re-render)
        await onUpdateSoapNote(updatedNote);
      }

      toast.success("Pronouns successfully applied to chart.");
      onCancel?.(); // close dropdown on success
    } catch (err) {
      const msg =
        err?.response?.data?.body?.message ||
        err?.message ||
        "Failed to apply pronouns to chart.";
      toast.error(msg);
      // Do NOT close dropdown on error – user may retry
    } finally {
      setIsProcessing(false);
      onEndProcessing?.();
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
      <div className="flex flex-col gap-4">
        {/* Vertical pronoun options */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => setSelectedPronouns("he/him")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all w-full sm:w-auto text-left ${
              selectedPronouns === "he/him"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50"
            }`}
          >
            He/Him
          </button>
          <button
            onClick={() => setSelectedPronouns("she/her")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all w-full sm:w-auto text-left ${
              selectedPronouns === "she/her"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50"
            }`}
          >
            She/Her
          </button>
          <button
            onClick={() => setSelectedPronouns("they/them")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all w-full sm:w-auto text-left ${
              selectedPronouns === "they/them"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50"
            }`}
          >
            They/Them
          </button>
        </div>

        {/* Action buttons row */}
        <div className="flex justify-between gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={isProcessing || !selectedPronouns} //disabled if no pronoun picked
            className="flex-1 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Applying…
              </>
            ) : (
              "Apply to chart"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}